import { FC, ElementType } from "react";
import DOMPurify from "dompurify";
import { Box } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import __ from "src/utils/Translations";

/**
 * Tagi HTML dozwolone w frazach tłumaczeń.
 *
 * Lista obejmuje standardowe, bezpieczne tagi formatowania tekstu (bez
 * wykonywania kodu, ładowania zasobów zewnętrznych czy interaktywności),
 * tak aby komponent był uniwersalny i nadawał się do użycia w dowolnym
 * module, a nie tylko pod konkretny przypadek.
 *
 * Świadomie POMINIĘTE (wymagałyby dodatkowych atrybutów i zabezpieczeń):
 * - "a" (linki)  -> href bywa wektorem (javascript:, phishing),
 * - "img"        -> onerror, ładowanie zasobów, tracking.
 * Dodanie któregokolwiek z nich powinno być osobną, świadomą decyzją wraz
 * z odpowiednią konfiguracją ALLOWED_ATTR i hookami DOMPurify.
 */
const ALLOWED_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "del",
  "ins",
  "mark",
  "small",
  "sub",
  "sup",
  "br",
  "wbr",
  "span",
  "p",
  "ul",
  "ol",
  "li",
  "blockquote",
  "code",
  "pre",
  "abbr",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
];

/**
 * Żadne atrybuty nie są dozwolone. Nawet przy szerokiej liście tagów brak
 * atrybutów eliminuje większość wektorów ataku (onclick, href, style, src).
 * Ewentualne dopuszczenie konkretnego atrybutu (np. href dla linków) powinno
 * być osobną, przemyślaną zmianą.
 */
const ALLOWED_ATTR: string[] = [];

/**
 * Zamienia znaki istotne dla HTML na encje, aby wartości zmiennych były
 * zawsze traktowane jako czysty tekst. Dzięki temu jedyne tagi w renderowanej
 * treści pochodzą z samej frazy tłumaczenia (text), a nie z argumentów —
 * co chroni integralność struktury HTML przy danych pochodzących od
 * użytkownika (np. nazwa stanowiska zawierająca znak "<").
 */
const escapeHtml = (value: string | number): string =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export interface TransHtmlProps {
  /**
   * Fraza źródłowa (po polsku) z opcjonalnymi tagami HTML, np.
   * "Lokalizacja <b>„{0}”</b> zostanie trwale usunięta z systemu."
   * Fraza jest tłumaczona przez __() wewnątrz komponentu.
   */
  text: string;
  /**
   * Argumenty wstrzykiwane w miejsca {0}, {1}, ... (jak w __()).
   */
  args?: Array<string | number>;
  /**
   * Element opakowujący. Domyślnie "span" (inline), aby komponent
   * dało się osadzić wewnątrz Typography/akapitów.
   */
  component?: ElementType;
  sx?: SxProps<Theme>;
  className?: string;
}

/**
 * Współdzielony komponent renderujący pojedynczą, scaloną frazę tłumaczenia,
 * która może zawierać standardowe tagi formatowania tekstu (np. pogrubienie,
 * kursywa, listy, nagłówki, łamanie wiersza) oraz zmienne w formacie {0}.
 *
 * Mechanizm jest sharowalny między modułami OMB (brak react-i18n), zgodnie
 * z F1-3411. Bezpieczeństwo:
 * - argumenty (args) są escapowane, więc tagi pochodzą wyłącznie z frazy
 *   tłumaczenia (text), nigdy ze zmiennych,
 * - treść jest sanityzowana przez DOMPurify z whitelistą bezpiecznych tagów
 *   formatowania i bez dozwolonych atrybutów.
 * Komponent jest przeznaczony dla fraz pochodzących z systemu tłumaczeń.
 */
const TransHtml: FC<TransHtmlProps> = ({
  text,
  args = [],
  component = "span",
  sx,
  className,
}) => {
  const translated = __(text, args.map(escapeHtml));
  const sanitized = DOMPurify.sanitize(translated, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });

  return (
    <Box
      component={component}
      className={className}
      sx={sx}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default TransHtml;
